import React, { useState, useEffect } from 'react';
import { CVSkillsSelectorProps } from './types';
import Button from '../UI/Button';
import { Plus, Save, X } from "lucide-react";

/**
 * CV Skills Selector component
 * Handles technical interests, soft skills, and hard skills selection with checkbox interfaces
 */
const CVSkillsSelector: React.FC<CVSkillsSelectorProps> = ({ 
  technicalInterests,
  softSkills,
  hardSkills,
  availableOptions,
  onUpdateTechnicalInterests,
  onUpdateSoftSkills,
  onUpdateHardSkills
}) => {
  // Technical Interests state
  const [selectedTechnicalInterests, setSelectedTechnicalInterests] = useState<string[]>([]);
  const [customTechnicalInterests, setCustomTechnicalInterests] = useState<string[]>([]);
  const [newCustomInterest, setNewCustomInterest] = useState('');
  
  // Soft Skills state
  const [selectedSoftSkills, setSelectedSoftSkills] = useState<string[]>([]);
  
  // Hard Skills state
  const [hardSkillsList, setHardSkillsList] = useState<string[]>([]);
  const [newHardSkill, setNewHardSkill] = useState('');
  
  // Loading states
  const [isUpdatingTechnical, setIsUpdatingTechnical] = useState(false);
  const [isUpdatingSoft, setIsUpdatingSoft] = useState(false);
  const [isUpdatingHard, setIsUpdatingHard] = useState(false);
  
  // Error states
  const [technicalError, setTechnicalError] = useState<string | null>(null);
  const [softError, setSoftError] = useState<string | null>(null);
  const [hardError, setHardError] = useState<string | null>(null);

  // Initialize state from props
  useEffect(() => {
    // Technical interests
    const predefinedInterests = technicalInterests
      .filter(interest => !interest.is_custom)
      .map(interest => interest.interest_name);
    const customInterests = technicalInterests
      .filter(interest => interest.is_custom)
      .map(interest => interest.interest_name);
    
    setSelectedTechnicalInterests(predefinedInterests);
    setCustomTechnicalInterests(customInterests);
    if(customInterests.length > 0) setNewCustomInterest(customInterests[0]);
    // Soft skills
    setSelectedSoftSkills(softSkills.map(skill => skill.skill_name));
    
    // Hard skills
    setHardSkillsList(hardSkills.map(skill => skill.skill_name));
  }, [technicalInterests, softSkills, hardSkills]);

  // Technical Interests handlers
  const handleTechnicalInterestChange = (interestName: string, checked: boolean) => {
    if (checked) {
      setSelectedTechnicalInterests(prev => [...prev, interestName]);
    } else {
      setSelectedTechnicalInterests(prev => prev.filter(name => name !== interestName));
    }
  };

  const handleCustomTechnicalInterestChange = (checked: boolean) => {
    if (checked) {
      setCustomTechnicalInterests([newCustomInterest.trim() || '-']);
    } else {
      setCustomTechnicalInterests([]);
    }
  };

  const handleAddCustomInterest = () => {
    if (newCustomInterest.trim() && !customTechnicalInterests.includes(newCustomInterest.trim())) {
      setCustomTechnicalInterests(prev => [...prev, newCustomInterest.trim()]);
      setNewCustomInterest('');
    }
  };

  const handleUpdateTechnicalInterests = async () => {
    setIsUpdatingTechnical(true);
    setTechnicalError(null);
    let newCustomTechnicalInterests = ['-']
    if(customTechnicalInterests.length > 0){
      newCustomTechnicalInterests[0] = newCustomInterest.trim() || '-';
    }else{
      newCustomTechnicalInterests = []
      setNewCustomInterest('')
    }
    try {
      await onUpdateTechnicalInterests(selectedTechnicalInterests, newCustomTechnicalInterests);
    } catch (error: any) {
      setTechnicalError(error.message || 'Failed to update technical interests');
    } finally {
      setIsUpdatingTechnical(false);
    }
  };

  // Soft Skills handlers
  const handleSoftSkillChange = (skillName: string, checked: boolean) => {
    if (checked) {
      setSelectedSoftSkills(prev => [...prev, skillName]);
    } else {
      setSelectedSoftSkills(prev => prev.filter(name => name !== skillName));
    }
  };

  const handleUpdateSoftSkills = async () => {
    setIsUpdatingSoft(true);
    setSoftError(null);
    
    try {
      await onUpdateSoftSkills(selectedSoftSkills);
    } catch (error: any) {
      setSoftError(error.message || 'Failed to update soft skills');
    } finally {
      setIsUpdatingSoft(false);
    }
  };

  // Hard Skills handlers
  const handleAddHardSkill = () => {
    if (newHardSkill.trim() && !hardSkillsList.includes(newHardSkill.trim())) {
      setHardSkillsList(prev => [...prev, newHardSkill.trim()]);
      setNewHardSkill('');
    }
  };

  const handleRemoveHardSkill = (skillName: string) => {
    setHardSkillsList(prev => prev.filter(name => name !== skillName));
  };

  const handleUpdateHardSkills = async () => {
    setIsUpdatingHard(true);
    setHardError(null);
    
    try {
      await onUpdateHardSkills(hardSkillsList);
    } catch (error: any) {
      setHardError(error.message || 'Failed to update hard skills');
    } finally {
      setIsUpdatingHard(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="cv-skills-selector">
      <div className="flex flex-wrap">
        <div className="w-full">
          {/* Technical Interests Section */}
          <div className="mb-5">
            <h4 className="mb-3 font-semibold">Minat Teknis</h4>
            <p className="text-gray-500 mb-3">
              Pilih bidang minat teknis Anda. Anda dapat memilih dari opsi yang
              tersedia dan menambahkan minat khusus.
            </p>

            {technicalError && (
              <div
                className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
                role="alert"
              >
                {technicalError}
              </div>
            )}

            {/* Predefined Technical Interests */}
            <div className="mb-3">
              <h5 className="mb-2">Opsi yang Tersedia</h5>
              <div className="flex flex-wrap">
                {availableOptions.technicalInterests.map((interest) => (
                  <div
                    key={interest}
                    className="w-full md:w-6/12 lg:w-4/12 mb-2"
                  >
                    <div className="form-check flex items-center gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`tech-${interest}`}
                        checked={selectedTechnicalInterests.includes(interest)}
                        onChange={(e) =>
                          handleTechnicalInterestChange(
                            interest,
                            e.target.checked
                          )
                        }
                        disabled={isUpdatingTechnical}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`tech-${interest}`}
                      >
                        {interest}
                      </label>
                    </div>
                  </div>
                ))}
                <div className="w-full md:w-6/12 lg:w-4/12 mb-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`tech-custom`}
                        checked={customTechnicalInterests.length > 0}
                        onChange={(e) =>
                          handleCustomTechnicalInterestChange(e.target.checked)
                        }
                        disabled={isUpdatingTechnical}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`tech-custom`}
                      >
                        Lainnya
                      </label>
                    </div>
                    {customTechnicalInterests.length > 0 && (
                      <input
                        type="text"
                        className="form-control w-full"
                        placeholder="Tambahkan minat teknis khusus"
                        value={newCustomInterest}
                        onChange={(e) => setNewCustomInterest(e.target.value)}
                        onKeyPress={(e) =>
                          handleKeyPress(e, handleAddCustomInterest)
                        }
                        disabled={isUpdatingTechnical}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                primary
                onClick={handleUpdateTechnicalInterests}
                disabled={isUpdatingTechnical}
                className="px-4 flex items-center gap-2 gradient-primary w-full"
              >
                <Save />{" "}
                {isUpdatingTechnical ? "Memperbarui..." : "Simpan Data"}
              </Button>
            </div>
          </div>
          <hr className="my-3" />
          {/* Soft Skills Section */}
          <div className="mb-5">
            <h4 className="mb-3 font-semibold">Soft Skill</h4>
            <p className="text-gray-500 mb-3">
              Pilih Soft Skill yang paling menggambarkan kemampuan dan gaya
              kerja Anda.
            </p>

            {softError && (
              <div
                className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
                role="alert"
              >
                {softError}
              </div>
            )}

            <div className="mb-3">
              <div className="flex flex-wrap">
                {availableOptions.softSkills.map((skill) => (
                  <div key={skill} className="w-full md:w-6/12 lg:w-4/12 mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`soft-${skill}`}
                        checked={selectedSoftSkills.includes(skill)}
                        onChange={(e) =>
                          handleSoftSkillChange(skill, e.target.checked)
                        }
                        disabled={isUpdatingSoft}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`soft-${skill}`}
                      >
                        {skill}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                primary
                onClick={handleUpdateSoftSkills}
                disabled={isUpdatingSoft}
                className="px-4 flex items-center gap-2 gradient-primary w-full"
              >
                <Save /> {isUpdatingSoft ? "Memperbarui..." : "Simpan Data"}
              </Button>
            </div>
          </div>
          <hr className="my-3" />
          {/* Hard Skills Section */}
          <div className="mb-4">
            <h4 className="mb-3 font-semibold">Hard Skill</h4>
            <p className="text-gray-500 mb-3">
              Tambahkan Hard Skill, bahasa pemrograman, alat, dan teknologi
              Anda.
            </p>

            {hardError && (
              <div
                className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
                role="alert"
              >
                {hardError}
              </div>
            )}

            <div className="mb-3">
              <div className="flex flex-wrap items-end">
                <div className="w-full md:w-8/12 mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Tambahkan Hard Skill (contoh: JavaScript, Python, React)"
                    value={newHardSkill}
                    onChange={(e) => setNewHardSkill(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleAddHardSkill)}
                    disabled={isUpdatingHard}
                  />
                </div>
                <div className="md:w-4/12 mb-2">
                  <Button
                    type="button"
                    primary
                    onClick={handleAddHardSkill}
                    disabled={!newHardSkill.trim() || isUpdatingHard}
                    block
                    className="flex items-center justify-center gap-2"
                  >
                    <Plus /> Tambah
                  </Button>
                </div>
              </div>

              {/* Display hard skills */}
              {hardSkillsList.length > 0 && (
                <div className="mt-2">
                  {hardSkillsList.map((skill) => (
                    <span
                      key={skill}
                      className="inline-block px-2 py-1 font-medium rounded bg-primary text-white mr-2 mb-2"
                    >
                      {skill}
                      <button
                        type="button"
                        className="btn-close btn-close-white ml-2 my-auto"
                        aria-label="Remove"
                        onClick={() => handleRemoveHardSkill(skill)}
                        disabled={isUpdatingHard}
                      >
                        <X size={"1rem"} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                primary
                onClick={handleUpdateHardSkills}
                disabled={isUpdatingHard}
                className="px-4 flex items-center gap-2 gradient-primary w-full"
              >
                <Save /> {isUpdatingHard ? "Memperbarui..." : "Simpan Data"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVSkillsSelector;